// src/components/bodyBlocks/signBlock/SignBlock.tsx

import { useEffect, useState } from "react";
import LoginForm from "../../forms/loginForm/LoginForm";
import RegisterForm from "../../forms/registerForm/RegisterForm";
import { Link } from "react-router-dom";
import { ButtonSiguiente } from "../../ui";
import { CardsBlockCss } from "../../styles";

/**
 * 🧩 SignBlock
 * Componente para la sección de acceso y registro:
 *  - Muestra formulario de login o registro según estado
 *  - Mensaje de éxito al registrar
 *  - Panel informativo y video promocional
 */
const SignBlock: React.FC = () => {
  // 🔹 Estado para alternar entre registro y login
  const [isRegistering, setIsRegistering] = useState(false);

  // 🔹 Mensaje de éxito tras registro
  const [successMessage, setSuccessMessage] = useState("");

  // 🟢 Efecto para modificar estilos del body y scroll
  useEffect(() => {
    document.body.classList.add("scroll-container");
    document.body.style.backgroundColor = "#52525B"; // Gris oscuro
    return () => {
      document.body.style.backgroundColor = "";
      document.body.classList.remove("scroll-container");
    };
  }, []);

  // 🔹 Alterna entre formulario de login y registro
  const toggleForm = () => {
    setIsRegistering(!isRegistering);
    setSuccessMessage(""); // Limpiar mensaje al cambiar de formulario
  };

  // 🔹 Callback cuando se registra un usuario correctamente
  const handleRegisterSuccess = () => {
    setSuccessMessage("Usuario creado correctamente");
    setIsRegistering(false); // Volver al login tras registro
  };

  const cardBaseClass =
    "flex-1 h-96 max-h-[500px] bg-black p-6 rounded-2xl shadow-2xl flex flex-col items-center justify-between text-center border-r-4 border-t-2 border-t-gray-500 hover:border-blue-700 transition-all duration-1000 ease-in-out";

  return (
    <div className="flex justify-center items-start lg:items-center min-h-screen p-4 mt-24 md:mt-0">
      <div className="flex flex-col lg:flex-row w-full max-w-5xl gap-4">
        {/* 🔹 Lado izquierdo: panel informativo */}
        <div
          className={`${cardBaseClass} ${!CardsBlockCss.profesion}`}
          style={{ transform: "none" }}
        >
          <h2 className="text-lg sm:text-xl font-bold text-white hover:text-blue-700">
            Eres empresa?
            <br />
            Eres autónomo?
            <br />
            Eres emprendedor?
            <br />
            <br />
            No dudes en suscribirte y alójate en una de las mejores AppWeb
            <br />
            que puedes encontrar en el mercado al alcance de todos en un par de
            clics!!!
          </h2>

          <Link
            to="/suscribirse"
            className="p-2 w-full items-start justify-start bg-zinc-600 shadow-md rounded-md border hover:border-blue-700 active:bg-white active:text-black text-white font-bold transition-colors duration-300 animate-border-pulse"
          >
            Suscríbete como Profesional
          </Link>
        </div>

        {/* 🔹 Lado derecho: formularios */}
        <div className={`${cardBaseClass}`}>
          <h2 className="font-bold text-white">
            {isRegistering ? "Regístrate" : "Acceso Registrados"}
          </h2>

          {/* Mensaje de éxito */}
          {successMessage && <p className="text-green-500">{successMessage}</p>}

          {/* Formulario de login o registro */}
          {isRegistering ? (
            <RegisterForm onSuccess={handleRegisterSuccess} />
          ) : (
            <LoginForm />
          )}

          {/* Botón para alternar entre login y registro */}
          <ButtonSiguiente
            onClick={toggleForm}
            className="w-full mt-4 font-bold hover:bg-gray-900 p-2"
          >
            {isRegistering ? "Logearse" : "Registrarse"}
          </ButtonSiguiente>
        </div>

        {/* 🔹 Panel de accesos rápidos */}
        <div
          className={`${cardBaseClass}
           relative
           overflow-hidden
           border
           border-white/20
           bg-black
           backdrop-blur-sm
           shadow-2xl`}
          style={{ transform: "none" }}
        >
          {/* Imagen de fondo */}
          <img
            src="/img/body/fondoBotoneraSing.jpeg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover scale-110 brightness-110 contrast-110 saturate-125 opacity-80"
          />

          {/* Degradado para mejorar la lectura */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-black/30 to-slate-900/50"></div>

          {/* Luces decorativas */}
          <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-cyan-400/30 blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 right-0 w-64 h-64 rounded-full bg-blue-500/20 blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-20 left-1/3 w-60 h-60 rounded-full bg-fuchsia-500/20 blur-3xl animate-pulse"></div>

          {/* Reflejo superior */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-transparent"></div>

          <div className="relative z-10 flex flex-col justify-center gap-6 w-full h-full">
            {/* BOTÓN 1 */}
            <a
              href="https://www.youtube.com/watch?v=VC9ZRyPQE0o"
              target="_blank"
              rel="noopener noreferrer"
              className="
              relative
              overflow-hidden
              w-full
              py-4
              rounded-2xl
              text-center
              font-bold
              text-white
          
              bg-white/[0.015]
              backdrop-blur-sm
          
              border
              border-cyan-300/30
          
              shadow-[0_0_6px_rgba(34,211,238,0.05)]
          
              before:absolute
              before:inset-0
              before:rounded-2xl
              before:bg-gradient-to-br
              before:from-white/5
              before:via-transparent
              before:to-transparent
              before:opacity-10
              before:pointer-events-none
          
              hover:bg-cyan-400/5
              hover:border-cyan-200/50
              hover:shadow-[0_0_12px_rgba(34,211,238,0.10)]
              hover:scale-105
              hover:-translate-y-1
          
              active:scale-95
          
              transition-all duration-500
              "
            >
              Qué es LinkServs?
            </a>

            {/* BOTÓN 2 */}
            <Link
              to="https://www.youtube.com/watch?v=AUcq4bXv3VM"
              target="_blank"
              className="
              relative overflow-hidden w-full py-4 rounded-2xl text-center font-bold text-white
          
              bg-white/[0.015]
              backdrop-blur-sm
          
              border border-emerald-300/30
          
              shadow-[0_0_6px_rgba(52,211,153,0.05)]
          
              before:absolute
              before:inset-0
              before:rounded-2xl
              before:bg-gradient-to-br
              before:from-white/5
              before:via-transparent
              before:to-transparent
              before:opacity-10
              before:pointer-events-none
          
              hover:bg-emerald-400/5
              hover:border-emerald-200/50
              hover:shadow-[0_0_12px_rgba(52,211,153,0.10)]
              hover:scale-105
              hover:-translate-y-1
          
              active:scale-95
          
              transition-all duration-500
              "
            >
              Cómo Suscribirse
            </Link>

            {/* BOTÓN 3 */}
            <Link
              to="https://www.youtube.com/watch?v=UF7PiEm5jtY&t=55s"
              target="_blank"
              className="
              relative overflow-hidden w-full py-4 rounded-2xl text-center font-bold text-white
          
              bg-white/[0.015]
              backdrop-blur-sm
          
              border border-fuchsia-300/30
          
              shadow-[0_0_6px_rgba(232,121,249,0.05)]
          
              before:absolute
              before:inset-0
              before:rounded-2xl
              before:bg-gradient-to-br
              before:from-white/5
              before:via-transparent
              before:to-transparent
              before:opacity-10
              before:pointer-events-none
          
              hover:bg-fuchsia-400/5
              hover:border-fuchsia-200/50
              hover:shadow-[0_0_12px_rgba(232,121,249,0.10)]
              hover:scale-105
              hover:-translate-y-1
          
              active:scale-95
          
              transition-all duration-500
              "
            >
              Cómo editar tu espacio
            </Link>

            {/* BOTÓN 4 */}
            <Link
              to="https://www.youtube.com/watch?v=26uXrt-ZIlo&t=3s"
              target="_blank"
              className="
              relative overflow-hidden w-full py-4 rounded-2xl text-center font-bold text-white
          
              bg-white/[0.015]
              backdrop-blur-sm
          
              border border-amber-300/30
          
              shadow-[0_0_6px_rgba(251,191,36,0.05)]
          
              before:absolute
              before:inset-0
              before:rounded-2xl
              before:bg-gradient-to-br
              before:from-white/5
              before:via-transparent
              before:to-transparent
              before:opacity-10
              before:pointer-events-none
          
              hover:bg-amber-400/5
              hover:border-amber-200/50
              hover:shadow-[0_0_12px_rgba(251,191,36,0.10)]
              hover:scale-105
              hover:-translate-y-1
          
              active:scale-95
          
              transition-all duration-500
              "
            >
              Funcionamiento
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignBlock;
